/**
 * One meaning per colour, across every module.
 *
 * 56 files currently define their own status -> colour map. "Draft" is neutral
 * slate in HR and warning amber in Safety; "Active" is green-100/700 in one
 * module and green-50/700 in the next; "Complete" comes in three greens. A
 * badge's colour is supposed to be information — when it is per-file opinion,
 * people learn to read the word and ignore the colour.
 *
 * Modules keep their own vocabulary. They map that vocabulary onto these five
 * tones instead of onto raw Tailwind classes.
 *
 * Colour values follow `docs/design-system/elevated-polish-guide.md`: the
 * in-brand tone uses the OPG blues as arbitrary values (the guide's own
 * notation, and `Technician/PageHero.tsx` holds the constants) rather than a
 * new Tailwind token, so there is one source of truth for the palette.
 */

export type StatusTone = 'neutral' | 'active' | 'attention' | 'blocked' | 'done';

/** What each tone means. Kept next to the classes so the two cannot drift. */
export const TONE_MEANING: Record<StatusTone, string> = {
  neutral: 'Not started, parked, or archived — nothing is owed.',
  active: 'In flight. Someone is working it right now.',
  attention: 'Needs a person: on hold, awaiting review, coming due.',
  blocked: 'Failed, cancelled, rejected, errored, or past due.',
  done: 'Finished, and finished well.',
};

/**
 * Badge fills. `ring-1` rather than `border`, per the guide — a ring paints
 * outside the box, so a badge never shifts the row it sits in.
 */
export const TONE_CLASSES: Record<StatusTone, string> = {
  neutral: 'bg-slate-100 ring-1 ring-slate-200 text-slate-700',
  active: 'bg-[#3FA5FF]/10 ring-1 ring-[#3FA5FF]/30 text-[#007ACC]',
  attention: 'bg-amber-50 ring-1 ring-amber-200 text-amber-800',
  blocked: 'bg-red-50 ring-1 ring-red-200 text-red-800',
  done: 'bg-emerald-50 ring-1 ring-emerald-200 text-emerald-800',
};

/** Solid fill, for dots, rails and kanban column headers. */
export const TONE_SOLID: Record<StatusTone, string> = {
  neutral: 'bg-slate-400',
  active: 'bg-[#007ACC]',
  attention: 'bg-amber-500',
  blocked: 'bg-red-600',
  done: 'bg-emerald-600',
};

export function toneClasses(tone: StatusTone): string {
  return TONE_CLASSES[tone];
}

/**
 * The shared vocabulary.
 *
 * Order BETWEEN tones matters: the first tone whose vocabulary matches wins, so
 * `blocked` is checked before `done` and "cancelled - complete" reads as
 * cancelled. Order WITHIN a tone does not.
 */
const VOCABULARY: ReadonlyArray<readonly [StatusTone, readonly string[]]> = [
  ['blocked', [
    'cancel', 'cancelled', 'canceled', 'void', 'voided', 'rejected', 'declined',
    'failed', 'failure', 'error', 'expired', 'lost', 'removed', 'terminated',
    'inactive', 'disabled', 'suspended', 'deleted', 'critical', 'overdue',
    'rolled_back', 'reverted', 'bounced',
    // "past due" IS "overdue" — the two must not land in different tones, or an
    // invoice reads as "needs a look" in one module and "blocked" in the next.
    'past due', 'past_due', 'past-due',
    // The literal word has to resolve to the tone that carries its name.
    'blocked',
  ]],
  ['attention', [
    'pending', 'awaiting', 'needs review', 'needs_review', 'review', 'on hold',
    'on_hold', 'hold', 'credit hold', 'at risk', 'at_risk', 'warning',
    'due', 'unassigned', 'draft', 'proposed', 'requested', 'submitted',
    'escalated', 'flagged', 'stale', 'partial', 'invited', 'paused',
  ]],
  ['active', [
    'active', 'in progress', 'in_progress', 'inprogress', 'progress', 'running',
    'open', 'dispatched', 'scheduled', 'assigned', 'started', 'working',
    'processing', 'syncing', 'live', 'published', 'confirmed', 'accepted',
    'approved', 'enabled', 'ordered', 'shipped', 'installing',
    'canary', 'testing', 'deploying', 'deployed', 'evaluating', 'production',
    'rolling_out', 'rolled_out',
  ]],
  ['done', [
    'complete', 'completed', 'closed', 'done', 'billed', 'invoiced', 'paid',
    'reviewed', 'finished', 'delivered', 'resolved', 'success', 'succeeded',
    'passed', 'signed', 'won', 'fulfilled', 'solved', 'transferred',
    'redeemed', 'retired',
  ]],
  ['neutral', [
    'new', 'not started', 'not_started', 'todo', 'backlog', 'archived', 'archive',
    'dormant', 'n/a', 'none', 'unknown', 'other', 'planned',
  ]],
];

/**
 * Resolve a module's own status string onto a tone.
 *
 * Unknown strings resolve to `neutral` rather than throwing — a status nobody
 * has classified yet should look unremarkable, not alarming. Pass an explicit
 * tone at the call site when a module's word means something the shared
 * vocabulary gets wrong.
 */
export function statusTone(status: string | null | undefined): StatusTone {
  if (!status) return 'neutral';
  const s = status.toLowerCase().trim();

  // Exact hit first — a status that IS the word beats one that merely contains
  // it, so "reviewed" resolves to done rather than being dragged to attention
  // by containing "review".
  for (const [tone, words] of VOCABULARY) {
    if (words.includes(s)) return tone;
  }
  // Then substring, longest vocabulary entry first, so "past due" beats "due".
  let best: { tone: StatusTone; len: number } | null = null;
  for (const [tone, words] of VOCABULARY) {
    for (const w of words) {
      if (s.includes(w) && (!best || w.length > best.len)) best = { tone, len: w.length };
    }
  }
  return best?.tone ?? 'neutral';
}

/** Convenience: status string straight to classes. */
export function statusClasses(status: string | null | undefined): string {
  return TONE_CLASSES[statusTone(status)];
}
